import { ChangeDetectionStrategy, Component, Input, computed, signal } from '@angular/core';
import {
  LucideAngularModule, LucideIconData,
  // Iconos del menú (mapeo desde nombres "DevExtreme legacy" que vienen del
  // backend via menu-mapping.ts → CLASS_TO_ICON / DEFAULT_ICON_BY_TEXT).
  User, Users, Settings, FileText, Folder, BarChart3,
  ShoppingCart, Mail, Bell, Calendar, ListChecks, Home,
  AlertTriangle, LayoutList,
  // Iconos UI / chrome del sidebar y header (chevrons, controls, etc.).
  ChevronRight, ChevronDown, ChevronLeft, ChevronUp,
  Search, X, MessageCircle, LogOut, Pin, Star,
  Sliders, Sun, Moon, Sparkles, PanelLeft, Plus, Check,
  CircleDot, Package, ShieldAlert, Box, FileSearch,
  RefreshCw, MoreHorizontal,
  // Iconos del toolbar interno de grilla (acciones, filtros, export).
  Clock, Download, Filter, Trash2, Edit3, Printer, Copy,
  Rows3, Rows4,
} from 'lucide-angular';

/**
 * Mapping nombre legacy (lo que envía el backend via `Modulo.class` o lo que
 * `menu-mapping.ts` deriva del texto) → componente Lucide.
 *
 * Los nombres a la IZQUIERDA son los strings que ya circulan por el código
 * (no los renombramos en navigation.service / menu-mapping para mantener el
 * contrato con backend y el resto del frontend). Los nombres a la DERECHA son
 * los componentes de Lucide.
 *
 * Lucide oficial: https://lucide.dev/icons/
 *
 * Si llega un nombre desconocido, se cae a `LayoutList` (igual que el
 * fallback de `detailslayout` que ya usaba menu-mapping).
 */
const LEGACY_ICON_TO_LUCIDE: Record<string, LucideIconData> = {
  // ===== Items del menu del backend =====
  user:             User,
  users:            Users,
  group:            Users,
  preferences:      Settings,
  doc:              FileText,
  file:             FileText,
  folder:           Folder,
  chart:            BarChart3,
  cart:             ShoppingCart,
  email:            Mail,
  mail:             Mail,
  bell:             Bell,
  belloutline:      Bell,
  event:            Calendar,
  calendar:         Calendar,
  check:            ListChecks,
  home:             Home,
  warning:          AlertTriangle,
  detailslayout:    LayoutList,
  product:          Package,
  package:          Package,
  card:             FileText,

  // ===== UI / chrome (sidebar, header, controls) =====
  chevronright:     ChevronRight,
  chevrondown:      ChevronDown,
  chevronleft:      ChevronLeft,
  chevronup:        ChevronUp,
  search:           Search,
  close:            X,
  remove:           X,
  clear:            X,
  message:          MessageCircle,
  chat:             MessageCircle,
  runner:           LogOut,
  logout:           LogOut,
  pin:              Pin,
  star:             Star,
  sliders:          Sliders,
  sun:              Sun,
  moon:             Moon,
  sparkles:         Sparkles,
  panelleft:        PanelLeft,
  menu:             PanelLeft,
  plus:             Plus,
  add:              Plus,
  ok:               Check,
  check2:           Check,
  shieldalert:      ShieldAlert,
  alerttriangle:    AlertTriangle,
  box:              Box,
  filesearch:       FileSearch,
  refresh:          RefreshCw,
  more:             MoreHorizontal,
  circledot:        CircleDot,
  clock:            Clock,
  download:         Download,
  export:           Download,
  filter:           Filter,
  trash:            Trash2,
  edit:             Edit3,
  print:            Printer,
  copy:             Copy,
  // Tres niveles de densidad — usado en el density toggle dropdown
  list:             Rows4,        // compact
  rows:             Rows3,        // spacious
  // info — icono para "Ver detalle" en context menu
  info:             FileSearch,
};

/**
 * <app-icon name="cart">  — wrapper standalone que renderiza un icono Lucide
 * a partir de los nombres legacy que circulan por el código (cart, group, etc.).
 *
 * Beneficios:
 *   - El backend / NavigationService / menu-mapping NO cambian — siguen mandando
 *     strings tipo "cart", "group", "belloutline".
 *   - Si quisieramos cambiar de set de iconos (Phosphor, Tabler, etc.), tocamos
 *     solo este componente.
 *   - Tree-shaking: solo importamos los iconos que usamos arriba, no toda la
 *     librería (~3000 iconos).
 *
 * Defaults:
 *   - size 18px (alineado con el `font-size` de los .nav-item-icon del sidebar)
 *   - strokeWidth 1.75 (peso intermedio entre 1.5 y 2 — más moderno que el
 *     default de Lucide de 2)
 *
 * `currentColor` por default — el SVG hereda el color del padre via CSS,
 * ideal para themed icons (.is-active → accent, etc.).
 */
@Component({
  selector: 'app-icon',
  standalone: true,
  imports: [LucideAngularModule],
  template: `
    <lucide-angular
      [img]="resolved()"
      [size]="size"
      [strokeWidth]="strokeWidth"
      [absoluteStrokeWidth]="true"
      aria-hidden="true"
      role="img">
    </lucide-angular>
  `,
  styles: [`
    :host { display: inline-flex; align-items: center; justify-content: center; line-height: 0; }
    :host lucide-angular { display: inline-flex; }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppIconComponent {
  private nameSig = signal<string>('');

  @Input()
  set name(value: string | null | undefined) {
    this.nameSig.set((value ?? '').toString().trim().toLowerCase());
  }

  @Input() size = 18;
  @Input() strokeWidth = 1.75;

  /** Icono Lucide resuelto a partir del nombre legacy. Si no hay match, fallback a LayoutList. */
  resolved = computed<LucideIconData>(() => {
    const key = this.nameSig();
    return LEGACY_ICON_TO_LUCIDE[key] ?? LayoutList;
  });
}
